/**
 * React Query key factory — CRM keys (clients, learners, contacts, lead types/
 * sources, professions, NIR refusal reasons, enterprises).
 *
 * Référence centrale dans `queryKeys.ts` (barrel).
 */

const clientsAll = ['clients'] as const
const learnersAll = ['learners'] as const
const contactsAll = ['contacts'] as const
const leadTypesAll = ['leadTypes'] as const
const leadSourcesAll = ['leadSources'] as const
const professionsAll = ['professions'] as const
const nirRefusalReasonsAll = ['nir-refusal-reasons'] as const
const enterprisesAll = ['enterprises'] as const

export const crmKeys = {
    clients: {
        all: clientsAll,
        list: (entityId: string, filters?: Record<string, unknown>) =>
            [...clientsAll, 'list', entityId, filters] as const,
        // `entityId` est inclus dans la clé : la réponse `is_inherited /
        // editable / deletable` dépend du viewer courant, deux entités
        // différentes ne partagent donc pas le même cache. Sans cela, le
        // bandeau ↑ Hérité reste figé après un switch d'entité (post-impl
        // étape 5 §5c).
        detail: (id: string, entityId: string) =>
            [...clientsAll, 'detail', id, entityId] as const,
        // Préfixe pour invalidation cross-entity (`invalidateQueries`) — matche
        // TOUTES les variantes en cache d'une même fiche, quelle que soit
        // l'entité du viewer.
        detailAny: (id: string) => [...clientsAll, 'detail', id] as const,
        nearby: (entityId: string, lat: number, lng: number, radiusKm: number) =>
            [...clientsAll, 'nearby', entityId, lat, lng, radiusKm] as const,
        // CLI-025 — Vue carte (bbox + recherche + filtres de colonnes hérités) +
        // carte de survol riche chargée à la demande.
        map: (entityId: string, query: string) => [...clientsAll, 'map', entityId, query] as const,
        mapCard: (id: string, entityId: string) =>
            [...clientsAll, 'mapCard', id, entityId] as const,
        // CLI-008 — Paramètres comptables + codes externes.
        accounting: (id: string) =>
            [...clientsAll, 'accounting', id] as const,
        externalCodes: (id: string) =>
            [...clientsAll, 'externalCodes', id] as const,
        // Lookup transverse (cross-entity) — pas de clé entity dans la query
        // car la valeur ne dépend pas de l'entité du viewer (lookup global).
        externalCodesLookup: (codeType: string, value: string) =>
            [...clientsAll, 'externalCodesLookup', codeType, value] as const,
        // CLI-011 — État de blocage (état courant + historique paginé +
        // check-allowed par action). Toutes les clés incluent l'`id` client.
        // `checkAllowed` inclut l'`action` pour permettre des stale différents
        // par action (UX : on n'invalide que le scope concerné).
        block: (id: string) => [...clientsAll, 'block', id] as const,
        blockHistory: (id: string) =>
            [...clientsAll, 'blockHistory', id] as const,
        blockCheckAllowed: (id: string, action: string) =>
            [...clientsAll, 'blockCheckAllowed', id, action] as const,
        // SALE-019 A3 — sous-onglet « Devis » de l'onglet Activités (CLI-006) et
        // KPI « Devis en cours » du bandeau (CLI-007). Sous `clientsAll` pour que
        // l'invalidation large d'une fiche les emporte, et parce qu'une mutation
        // de devis doit les rafraîchir (doctrine _INTRO §6bis.2).
        proposals: (id: string, linkableOnly = false) =>
            [...clientsAll, 'proposals', id, linkableOnly] as const,
        proposalsAny: (id: string) => [...clientsAll, 'proposals', id] as const,
        openQuotes: (id: string) => [...clientsAll, 'openQuotes', id] as const,
        // CLI-010 — Prise en charge (défauts financiers + financeurs habituels).
        funding: (id: string) => [...clientsAll, 'funding', id] as const,
        // CLI-014 — Contraintes organisationnelles de planification (singleton).
        constraints: (id: string) => [...clientsAll, 'constraints', id] as const,
        // CLI-013 — Préférences formateurs (collection). Pas d'`entityId` dans la
        // clé : la réponse ne dépend pas du lecteur (aucun `is_inherited`), elle
        // appartient à la fiche cliente.
        instructorPreferences: (id: string) =>
            [...clientsAll, 'instructorPreferences', id] as const,
        // Résolution pour la planification (SESS-*) — dépend du contexte demandé.
        instructorPreferenceLookup: (id: string, siteId?: string, productId?: string) =>
            [...clientsAll, 'instructorPreferenceLookup', id, siteId, productId] as const,
        // CLI-009 — Paramètres de facturation (settings consolidés + sous-listes).
        // La clé `billing` racine est la lecture agrégée GET /billing ; les
        // sous-listes (einvoiceAddresses, b2gTriplets, scheduleTemplates) sont
        // exposées comme clés dédiées pour permettre des invalidations fines
        // sans repuller toute la vue.
        billing: (id: string) => [...clientsAll, 'billing', id] as const,
        billingEinvoiceAddresses: (id: string) =>
            [...clientsAll, 'billingEinvoiceAddresses', id] as const,
        billingB2GTriplets: (id: string) =>
            [...clientsAll, 'billingB2GTriplets', id] as const,
        billingScheduleTemplates: (id: string) =>
            [...clientsAll, 'billingScheduleTemplates', id] as const,
        // Numéros de marché des contrats cadres du client — suggestions de
        // saisie du champ « N° de marché » des identifiants Chorus Pro. Clé
        // SÉPARÉE de `billing` : les mutations du triplet ne la changent pas,
        // et une saisie libre ne crée jamais de contrat cadre.
        billingMarketNumbers: (id: string) =>
            [...clientsAll, 'billingMarketNumbers', id] as const,
        // CLI-009 v3 (2026-05-18) — éligibilité juridique + résumé funding light
        billingEligibility: (id: string) =>
            [...clientsAll, 'billingEligibility', id] as const,
        // CLI-009 (mig 223) — modèles d'échéancier INSTALLMENTS (entité, partagés).
        billingInstallmentPresets: (id: string) =>
            [...clientsAll, 'billingInstallmentPresets', id] as const,
        // CLI-009 (mig 225) — modèles d'échéancier CUSTOM (entité, partagés).
        billingCustomPresets: (id: string) =>
            [...clientsAll, 'billingCustomPresets', id] as const,
        fundingActiveSummaryV3: (id: string) =>
            [...clientsAll, 'fundingActiveSummaryV3', id] as const,
        // CLI-009 v2 — Liste IBAN entité (sélecteur BillingBankSelector).
        // Cache court (60s) car l'admin la gère dans Paramètres > Banque.
        entityBankAccounts: (id: string) =>
            [...clientsAll, 'entityBankAccounts', id] as const,
    },
    // CLI-012 — Contrats cadre (grilles tarifaires négociées par client).
    frameworkContracts: {
        byClient: (clientId: string) =>
            ['frameworkContracts', 'byClient', clientId] as const,
        detail: (contractId: string) =>
            ['frameworkContracts', 'detail', contractId] as const,
        prices: (contractId: string) =>
            ['frameworkContracts', 'prices', contractId] as const,
        // TND-011 — le volet « Engagement & conditions ». `all` sert aux
        // invalidations larges : le badge « marché public » et les colonnes
        // d'engagement de la table globale lisent le même fait autrement.
        marketTerms: (contractId: string) =>
            ['frameworkContracts', 'marketTerms', contractId] as const,
        // TND-013 — la vue transverse « Révisions de prix ». Sous le préfixe
        // `all` : toute écriture qui invalide `all` (volet, chaînage) la rafraîchit.
        priceRevisions: (entityId: string) =>
            ['frameworkContracts', 'priceRevisions', entityId] as const,
        /** Le COMPTE seul — ce que lit la barre latérale, sur chaque écran. Clé
         *  DISTINCTE de la liste : partager la clé ferait écraser un scalaire par
         *  une liste (et l'inverse) dans le cache de requêtes. */
        priceRevisionsCount: (entityId: string) =>
            ['frameworkContracts', 'priceRevisionsCount', entityId] as const,
        all: ['frameworkContracts'] as const,
    },
    // CLI-023 — Analyses des besoins (preuve Qualiopi I4).
    needsAnalyses: {
        byClient: (clientId: string, filters?: Record<string, unknown>) =>
            ['needsAnalyses', 'byClient', clientId, filters] as const,
        detail: (analysisId: string) =>
            ['needsAnalyses', 'detail', analysisId] as const,
        // DOC-008 — suivi d'envoi par dispatch (historique enrichi). Coexiste avec
        // les futures `signatureKeys` (SIGN-008) fusionnées par version dans le suivi.
        dispatchHistory: (analysisId: string) =>
            ['needsAnalyses', 'dispatchHistory', analysisId] as const,
    },
    // CLI-012 — Résolveur de prix `/api/pricing/price-lookup` (stale 60s).
    pricing: {
        lookup: (
            productId: string,
            labelId: string | null,
            unit: string,
            clientId: string | null,
            atDate: string | null,
        ) => ['pricing', 'lookup', productId, labelId, unit, clientId, atDate] as const,
    },
    learners: {
        // LRN-001 — pattern strict iso clients (entityId dans la clé du détail
        // pour switch d'entité automatique sans rafraîchir la page).
        all: learnersAll,
        list: (entityId: string, filters?: Record<string, unknown>) =>
            [...learnersAll, 'list', entityId, filters] as const,
        detail: (id: string, entityId: string) =>
            [...learnersAll, 'detail', id, entityId] as const,
        detailAny: (id: string) => [...learnersAll, 'detail', id] as const,
        // LRN-014 — Vue carte (bbox + recherche + filtres hérités) + carte de survol.
        map: (entityId: string, query: string) => [...learnersAll, 'map', entityId, query] as const,
        mapCard: (id: string, entityId: string) =>
            [...learnersAll, 'mapCard', id, entityId] as const,
        countByStatus: (entityId: string, includeDescendants: boolean) =>
            [
                ...learnersAll,
                'count-by-status',
                entityId,
                includeDescendants,
            ] as const,
    },
    // CLI-003 — Contacts du client (5 rôles + portail délégué CPORT-001)
    contacts: {
        all: contactsAll,
        byClient: (
            clientId: string,
            entityId: string,
            filters?: Record<string, unknown>,
        ) =>
            [
                ...contactsAll,
                'byClient',
                clientId,
                entityId,
                filters,
            ] as const,
        detail: (id: string) =>
            [...contactsAll, 'detail', id] as const,
        // `search` (page transverse /crm/contacts) retiré le 2026-05-02 —
        // remplacé par DIR-001 (Annuaire global, à implémenter).
        lookupEmail: (email: string, entityId: string) =>
            [...contactsAll, 'lookup', email, entityId] as const,
    },
    leadTypes: {
        all: leadTypesAll,
        list: (entityId: string) => [...leadTypesAll, 'list', entityId] as const,
    },
    leadSources: {
        all: leadSourcesAll,
        byType: (typeId: string) =>
            [...leadSourcesAll, 'byType', typeId] as const,
        list: (entityId: string) =>
            [...leadSourcesAll, 'list', entityId] as const,
    },
    // LRN-002 — référentiel des professions
    professions: {
        all: professionsAll,
        list: (entityId: string, search?: string) =>
            [...professionsAll, 'list', entityId, search ?? ''] as const,
        usage: (professionId: string) =>
            [...professionsAll, 'usage', professionId] as const,
    },
    // LRN-002bis — référentiel des motifs de refus du NIR
    nirRefusalReasons: {
        all: nirRefusalReasonsAll,
        list: (entityId: string, search?: string) =>
            [
                ...nirRefusalReasonsAll,
                'list',
                entityId,
                search ?? '',
            ] as const,
        usage: (reasonId: string) =>
            [...nirRefusalReasonsAll, 'usage', reasonId] as const,
    },
    // CLI-002 — Recherche Entreprises (proxy backend) + business sectors
    enterprises: {
        all: enterprisesAll,
        search: (q: string, limit?: number) =>
            [...enterprisesAll, 'search', q, limit ?? 5] as const,
        detail: (siren: string) =>
            [...enterprisesAll, 'detail', siren] as const,
    },
}
